import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AgGrid, AgGridReact, Banner, Flex, Grid, SegmentedControl, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { NewTemplateButton } from "~/pages/Reporting/ArenaDetail/components/NewTemplateButton";
import { useFetchTemplatesByArena } from "~/pages/Reporting/ArenaDetail/hooks";
import { ReportTemplate } from "~/pages/Reporting/ArenaDetail/types";
import { SkeletonLoaderCell } from "~/pages/Reporting/ArenaList/components/SkeletonLoaderCell";
import { REPORTING_PAGE_SIZE } from "~/pages/Reporting/constants";
import { getReportTypeLabel } from "~/pages/Reporting/ReportTemplateEditor/types";
import {
  dateFieldComparator,
  formatArchivedByLabel,
  formatUpdatedByLabel,
  formatUserName,
} from "~/pages/Reporting/utils";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import * as styles from "./styles_vanilla.css";

type ViewFilter = "active" | "archived";

const SYSTEM_ARENA_ID = null;
const SKELETON_ROW_COUNT = 10;

const SEGMENT_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Archived", value: "archived" },
];

const EMPTY_STATE_CONFIG: Record<ViewFilter, { title: string; subtitle?: string }> = {
  active: {
    title: "No system templates yet",
    subtitle: "Create a new system template to get started",
  },
  archived: {
    title: "No archived templates yet",
  },
};

const VALID_VIEW_FILTERS: ViewFilter[] = ["active", "archived"];

type NoRowsOverlayProps = { title: string; subtitle?: string };

const NoRowsOverlay = ({ title, subtitle }: NoRowsOverlayProps) => (
  <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="16px">
    <Icon type="file-alt" size="xxx-large" color={Colors.Neutral600} />
    <Text variant="secondary-xxl">{title}</Text>
    {subtitle && <Text variant="secondary-md">{subtitle}</Text>}
  </Flex>
);

function parseViewFilter(search: string): ViewFilter {
  const raw = new URLSearchParams(search).get("view");
  return VALID_VIEW_FILTERS.includes(raw as ViewFilter) ? (raw as ViewFilter) : "active";
}

type SystemTemplateRow = ReportTemplate & { isSkeletonRow?: boolean };

const SKELETON_ROWS: SystemTemplateRow[] = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index) =>
    ({
      id: index,
      name: "",
      reportType: "INVESTOR_REPORT",
      description: null,
      arenaId: null,
      createdAt: "",
      updatedAt: "",
      createdBy: 0,
      updatedBy: 0,
      updatedByUser: null,
      currentVersion: null,
      isSkeletonRow: true,
    }) as SystemTemplateRow,
);

const SkeletonOrValueCell = (
  params: AgGridReact.CustomCellRendererProps<SystemTemplateRow>,
): React.ReactNode | string => {
  if (params.data?.isSkeletonRow) {
    return <SkeletonLoaderCell />;
  }
  return String(params.valueFormatted ?? params.value ?? "-");
};

export const SystemTemplatesList = () => {
  const history = useHistory();
  const location = useLocation();
  const { templates, queryResult } = useFetchTemplatesByArena(SYSTEM_ARENA_ID);
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();

  const viewFilter = useMemo(() => parseViewFilter(location.search), [location.search]);

  const setViewFilter = useCallback(
    (value: ViewFilter) => {
      const params = new URLSearchParams(location.search);
      if (value === "active") {
        params.delete("view");
      } else {
        params.set("view", value);
      }
      const search = params.toString();
      history.push({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      });
    },
    [history, location.search, location.pathname],
  );

  const activeTemplates = useMemo(
    () => templates.filter((t) => t.isActive !== false),
    [templates],
  );

  const archivedTemplates = useMemo(
    () => templates.filter((t) => t.isActive === false),
    [templates],
  );

  const filteredTemplates = viewFilter === "active" ? activeTemplates : archivedTemplates;

  const rowData: SystemTemplateRow[] = queryResult.loading
    ? SKELETON_ROWS
    : filteredTemplates.map((t) => ({ ...t, isSkeletonRow: false }));

  const { gridOptions: baseGridOptions } = useAgGridDefaults({
    overrides: {
      paginationPageSize: REPORTING_PAGE_SIZE,
      pagination: true,
      rowHeight: 60,
    },
  });

  const gridOptions = useMemo(
    () => ({ ...baseGridOptions, overlayNoRowsTemplate: undefined }),
    [baseGridOptions],
  );

  const columnDefs = useMemo<AgGrid.ColDef<SystemTemplateRow>[]>(() => {
    const isActive = viewFilter === "active";
    return [
      {
        field: "name",
        headerName: "Template name",
        sortable: true,
        sort: isActive ? undefined : ("asc" as const),
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
      },
      {
        headerName: "Report type",
        sortable: true,
        filter: true,
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
        valueGetter: (params) => {
          if (params.data?.isSkeletonRow) return "";
          return getReportTypeLabel(params.data?.reportType);
        },
      },
      {
        headerName: "Data package",
        sortable: true,
        filter: true,
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
        valueGetter: (params: AgGrid.ValueGetterParams<ReportTemplate>) =>
          params.data?.dataPackage?.name ?? "-",
      },
      {
        headerName: isActive ? "Last updated" : "Archived by",
        sortable: true,
        filter: true,
        sort: isActive ? ("desc" as const) : undefined,
        flex: 2,
        cellRenderer: SkeletonOrValueCell,
        valueGetter: (params) => {
          if (params.data?.isSkeletonRow) return "";
          if (isActive) {
            return formatUpdatedByLabel(
              params.data?.updatedAt,
              formatUserName(params.data?.updatedByUser),
              formatDateWithTimezone,
            );
          }
          return formatArchivedByLabel(
            params.data?.archivedAt,
            formatUserName(params.data?.archivedByUser),
            formatDateWithTimezone,
          );
        },
        comparator: dateFieldComparator<SystemTemplateRow>((d) =>
          isActive ? d?.updatedAt : d?.archivedAt,
        ),
      },
    ];
  }, [formatDateWithTimezone, viewFilter]);

  const handleRowClick = useCallback(
    (event: AgGrid.RowClickedEvent<SystemTemplateRow>) => {
      if (event.data && !event.data.isSkeletonRow) {
        history.push(`/reporting/system/templates/${event.data.id}/view`);
      }
    },
    [history],
  );

  return (
    <Flex flexGrow={1} gap={16} flexDirection="column" className={styles.container}>
      <Flex justifyContent="space-between" flex={1} alignItems="center">
        <Text variant="h3">System templates</Text>
        <Flex gap={12} alignItems="center">
          <SegmentedControl
            value={viewFilter}
            onChange={(val) => setViewFilter(val as ViewFilter)}
            options={SEGMENT_OPTIONS}
          />
          <NewTemplateButton arenaId={null} hasTemplates={activeTemplates.length > 0} />
        </Flex>
      </Flex>
      {queryResult.error ? (
        <Banner
          variant="danger"
          title="We couldn't load system templates"
          description="There was a problem fetching system templates. Please try again later."
        />
      ) : (
        <Flex flexGrow={1}>
          <Grid<SystemTemplateRow>
            key={viewFilter}
            suppressServerSideFullWidthLoadingRow
            suppressNoRowsOverlay={queryResult.loading}
            reactiveCustomComponents
            noRowsOverlayComponent={NoRowsOverlay}
            noRowsOverlayComponentParams={EMPTY_STATE_CONFIG[viewFilter]}
            rowData={rowData}
            getRowId={(params) => String(params.data.id)}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            onRowClicked={handleRowClick}
          />
        </Flex>
      )}
    </Flex>
  );
};
